import { WITHHELD_LABEL } from '@cwos/api-client';
import type { documentation, schedule, timekeeping } from '@cwos/api-client';

import { parseDayOrInstant } from '../../lib/timeFormat';

/**
 * Presentation logic for the worker web experience.
 *
 * FORMATTING ONLY — deliberately. The worker app and this screen must agree
 * about whether somebody is clocked in, what they are owed and what is
 * scheduled, and the only way to guarantee that is for neither of them to
 * decide. Every status here is the server's; this file turns it into a phrase.
 *
 * There is no function below that decides whether a shift was missed, whether a
 * note is late, or whether a session should have closed. Those live in the API,
 * which is what the mobile app reads too.
 */

export type WorkerTab = 'today' | 'schedule' | 'time' | 'notes' | 'profile';

export const WORKER_TABS: { key: WorkerTab; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'time', label: 'My time' },
  { key: 'notes', label: 'Notes' },
  { key: 'profile', label: 'Profile' },
];

/** "8:15 AM". */
export function clockTime(iso: string | null | undefined, locale?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}

/**
 * "Mon, Aug 17" under en-US. The option ORDER below does not drive the output
 * order — the locale does.
 *
 * Takes either a calendar day ("2026-08-17") or an instant — see
 * parseDayOrInstant for why a bare day cannot go through `new Date`.
 */
export function dayLabel(iso: string | null | undefined, locale?: string): string {
  if (!iso) return '—';
  const d = parseDayOrInstant(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
}

/** "7h 20m" / "45m". Minutes as the server computed them. */
export function hours(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export interface ClockState {
  clockedIn: boolean;
  headline: string;
  detail: string;
}

/**
 * What the worker's clock is doing, in a sentence.
 *
 * Past tense about the past. An open session says when it started, not that
 * somebody "is at work" — the product knows a clock event happened, not where
 * a person is now, and the worker's own screen should not overclaim either.
 */
export function clockState(
  session: timekeeping.TimeSession | null | undefined,
  locale?: string,
): ClockState {
  if (!session || session.status !== 'open') {
    return {
      clockedIn: false,
      headline: 'Not clocked in',
      detail: 'You have no open session right now.',
    };
  }
  return {
    clockedIn: true,
    headline: 'Clocked in',
    detail: `Since ${clockTime(session.clockedInAt, locale)}.`,
  };
}

/** The shifts that fall on a given local day, in start order. */
export function shiftsOn(week: schedule.ScheduleWeek | undefined, day: Date): schedule.WeekShift[] {
  const target = day.toDateString();
  return (week?.shifts ?? [])
    .filter((s) => new Date(s.startsAt).toDateString() === target)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

/**
 * How a shift reads to the person who is meant to work it.
 *
 * `session` being present is the server's statement that a clock session
 * fulfilled this shift; its absence for a shift that has not started yet is
 * simply "upcoming", not a failure. Nothing here infers a no-show — the API
 * owns `status`.
 */
export function shiftLine(shift: schedule.WeekShift, locale?: string): string {
  const window = `${clockTime(shift.startsAt, locale)} – ${clockTime(shift.endsAt, locale)}`;
  if (shift.status === 'cancelled') return `${window} · cancelled`;
  if (shift.session) {
    // THE WITHHOLDING IS CHECKED FIRST, and this line is why.
    //
    // The server DECLARES the withholding with `punchFactsWithheld`; it does
    // not have to be inferred from which fields came back null. Inferring it
    // is the same mistake one level up — treating an absence as evidence of a
    // refusal — and it is wrong in both directions: a genuinely open session
    // also has a null `computedMinutes`, so inference reports the withheld
    // case and the live case identically.
    //
    // This branch used to read `computedMinutes != null ? "... recorded" :
    // "· clocked in"`, so a reader who may not see the punch facts was told the
    // person is CURRENTLY AT WORK — for a session that closed hours ago. Not an
    // absence standing in for a withholding but a different fact about the
    // world substituted for it, which is worse, and it was reachable: this
    // screen takes a workerId and is gated on `schedule:read`, so an actor
    // without `timekeeping:read_all` looking at somebody who does not report to
    // them landed here.
    if (shift.session.punchFactsWithheld) return `${window} · ${WITHHELD_LABEL}`;
    return shift.session.computedMinutes != null
      ? `${window} · ${hours(shift.session.computedMinutes)} recorded`
      : `${window} · clocked in`;
  }
  return window;
}

/** Notes the worker still owes, newest first. The API scopes this to them. */
export function notesOwed(
  items: documentation.ToWriteItem[] | undefined,
): documentation.ToWriteItem[] {
  return [...(items ?? [])].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

/**
 * The one-line summary at the top of Today.
 *
 * Counts only. A worker opening this wants to know whether anything is waiting
 * on them, and a number they can act on beats a paragraph they have to read.
 *
 * EVERY COUNT IS NULLABLE, and that is the whole of the fix. The caller built
 * all three from `?? []` lengths and `unread.data ?? 0`, so a failed schedule
 * read produced `shiftsToday: 0` and this function produced "Nothing scheduled
 * today" — the sentence that decides whether somebody goes to work, in the
 * headline, from a request that failed. The panel twenty lines below it had
 * already been fixed for exactly this and the headline above it had not.
 *
 * `null` means "not answered": pending, failed, or refused. It is not a zero
 * and it is never rounded down to one.
 */
export function todaySummary(input: {
  shiftsToday: number | null;
  notesOwed: number | null;
  unreadNotifications: number | null;
}): string {
  const parts: string[] = [];
  parts.push(
    input.shiftsToday === null
      ? 'Today’s schedule didn’t load'
      : input.shiftsToday === 0
        ? 'Nothing scheduled today'
        : `${input.shiftsToday} shift${input.shiftsToday === 1 ? '' : 's'} today`,
  );
  // The other two are ADDITIVE — they appear only when there is something to
  // report — so an unknown is silent rather than reassuring. A missing "3 notes
  // to write" understates the day; a fabricated "nothing to write" would be a
  // claim. Only the first clause is load-bearing enough to name its own failure.
  if (input.notesOwed !== null && input.notesOwed > 0) {
    parts.push(`${input.notesOwed} note${input.notesOwed === 1 ? '' : 's'} to write`);
  }
  if (input.unreadNotifications !== null && input.unreadNotifications > 0) {
    parts.push(`${input.unreadNotifications} unread`);
  }
  return parts.join(' · ');
}
